import React, {ReactNode} from 'react';
import {createJsonFromBoundMedia} from './utils';
import {revert079DataMigrationLocally} from '@packages/shared/utils/LegacyLightboxUtils';
import {
  Plugins,
  withSystem,
  withMeta,
  Atom,
  AtomRenderProps,
} from '@packages/systems/wfdl';
import {SHARED_ANCHOR_NODE_CONSTRAINTS_DESCENDANTS} from '@packages/systems/core/constants/SharedConstraintRules';
import {iconName, qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {
  TListOfElement,
  TRecord,
  TTypeConstructor,
  TList,
  TText,
} from '@packages/systems/wfdl-ast';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {
  EvaluatedNodeData,
  getChildren,
  getInPojo,
  getRecordOrElse,
} from '@packages/utilities/data-utils';
import {useSiteSearchIndexMode} from '@packages/systems/render';

const createMarkup = (value: string) => {
  return value ? {__html: value} : null;
};

const LightboxWrapper = Atom.createRender(
  ({
    attributes,
    classes,
    data,
    src,
    media,
    generateAttributes,
    renderChild,
  }: AtomRenderProps) => {
    const siteSearchIndexMode = useSiteSearchIndexMode();
    const lightboxClasses = classes.push('w-lightbox');
    const nodeJsonData = getRecordOrElse(getInPojo(data, ['json']), {});
    const oldNodeJsonData =
      // @ts-expect-error - nodeJsonData is loosely-typed evaluated renderer data
      revert079DataMigrationLocally(nodeJsonData);
    const json =
      // @ts-expect-error - nodeJsonData is loosely-typed evaluated renderer data
      createJsonFromBoundMedia(media || src, nodeJsonData) || oldNodeJsonData;

    const children: ReactNode = getChildren(data).map(
      (child: EvaluatedNodeData) => renderChild(child)
    );
    return (
      <a {...generateAttributes(attributes, lightboxClasses)}>
        {children}
        {json && !siteSearchIndexMode ? (
          <script
            type="application/json"
            className="w-json"
            // @ts-expect-error - TS2322 - Type '{ __html: string; } | null' is not assignable to type '{ __html: string | TrustedHTML; } | undefined'.
            dangerouslySetInnerHTML={createMarkup(
              JSON.stringify(json, null, 2)
            )}
          />
        ) : null}
      </a>
    );
  }
);

const config = createIntegratedAtom({
  displayName: 'Lightbox Link',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Lightbox'), iconName('Lightbox')),
  },
  showEmpty: true,
  constraints: {
    descendants: SHARED_ANCHOR_NODE_CONSTRAINTS_DESCENDANTS,
  },
  render: LightboxWrapper,
  fields: {
    attr: withSystem({private: true}, TRecord({href: TText})),
    lightbox: withSystem(
      {private: true},
      TRecord({type: withSystem({defaultValue: 'wrapper'}, TText)})
    ),
    block: withSystem({private: true}, TText),
    json: TRecord({
      items: withSystem(
        {
          label: 'Media',
          isBindable: true,
          isFullWidth: true,
          defaultValue: [],
          isLocalizable: true,
        },
        withMeta(
          {sortable: true},
          TList(
            TTypeConstructor(
              qualify(Plugins.name('Lightbox'), typeName('LightboxMedia'))
            )
          )
        )
      ),
      group: withMeta(
        {
          placeholder: 'e.g. Sunset Collection',
        },
        withSystem(
          {
            isFullWidth: true,
            label: 'Match or create a group name',
            isLocalizable: true,
          },
          TText
        )
      ),
    }),
    children: withSystem({private: true}, TListOfElement),
  },
  showSettingsInDescendants: true,
});

export default config;
